import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';

import FormButton from '../../../components/common/stories/FormButton';
import { useNotification } from '../../../context/NotificationContext';

const ThemeEditor = ({ website, onUpdate, isSaving }) => {
  const { getAccessTokenSilently } = useAuth0();
  const notification = useNotification();
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [primaryColor, setPrimaryColor] = useState(website?.theme?.primary_color || '#ff8264');
  const [logoUrl, setLogoUrl] = useState(website?.theme?.logo_url || '');
  const [bannerUrl, setBannerUrl] = useState(website?.theme?.banner_image_url || '');
  const [isUploading, setIsUploading] = useState(false);

  // Debug: Log when logoUrl changes
  console.log('ThemeEditor render - logoUrl:', logoUrl);

  useEffect(() => {
    // Only sync from props when website ID changes (initial load or switching websites)
    // Don't reset local state on every parent re-render
    setPrimaryColor(website?.theme?.primary_color || '#ff8264');
    setLogoUrl(website?.theme?.logo_url || '');
    setBannerUrl(website?.theme?.banner_image_url || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website?._id]);

  const handleSave = async () => {
    await onUpdate({
      theme: {
        primary_color: primaryColor,
        logo_url: logoUrl,
        banner_image_url: bannerUrl,
      },
    });
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      notification.error('Image must be less than 5MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      notification.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    try {
      setIsUploading(true);
      const token = await getAccessTokenSilently();
      console.log('Auth token obtained:', token ? 'Yes' : 'No', token?.substring(0, 20) + '...');

      // Get signed URL
      const fileName = `${type}-${Date.now()}-${file.name}`;
      const signedUrlResponse = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/s3/signed-url?type=store-asset&fileName=${encodeURIComponent(fileName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!signedUrlResponse.ok) {
        const errorText = await signedUrlResponse.text();
        console.error('Signed URL error:', signedUrlResponse.status, errorText);
        throw new Error(`Failed to get upload URL: ${signedUrlResponse.status}`);
      }

      const { signedUrl, fileUrl } = await signedUrlResponse.json();

      // Upload to S3
      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      // Update state
      console.log('Upload complete. File URL:', fileUrl);
      console.log('Current logoUrl before update:', logoUrl);
      if (type === 'logo') {
        setLogoUrl(fileUrl);
        console.log('Called setLogoUrl with:', fileUrl);
      } else {
        setBannerUrl(fileUrl);
      }

      notification.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      notification.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (type) => {
    if (type === 'logo') {
      setLogoUrl('');
    } else {
      setBannerUrl('');
    }
  };

  return (
    <div className="settings-form">
      {/* Color Settings */}
      <div className="theme-section">
        <h4 className="form-section-title">Brand Colors</h4>

        <div className="color-picker-row">
          <label>Primary Color:</label>
          <input
            type="color"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
          />
          <input
            type="text"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            placeholder="#ff8264"
          />
        </div>
      </div>

      {/* Logo Upload */}
      <div className="theme-section">
        <h4 className="form-section-title">Store Logo</h4>
        <p className="helper-text">
          Upload your store logo. Recommended size: 200x200 pixels, PNG or SVG format.
        </p>

        <div className="image-upload-section">
          <div className="image-preview logo">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Store logo"
                onError={(e) => console.error('Image load error:', e.target.src)}
                onLoad={() => console.log('Image loaded successfully')}
              />
            ) : (
              <span style={{ color: '#999' }}>No logo</span>
            )}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
              style={{ display: 'none' }}
              disabled={isUploading}
            />
            <FormButton type="button" disabled={isUploading} onClick={() => logoInputRef.current?.click()}>
              <UploadIcon style={{ marginRight: 5, fontSize: 18 }} />
              {isUploading ? 'Uploading...' : 'Upload Logo'}
            </FormButton>
            {logoUrl && (
              <IconButton onClick={() => handleRemoveImage('logo')} size="small">
                <DeleteIcon />
              </IconButton>
            )}
          </div>
        </div>
      </div>

      {/* Banner Upload */}
      <div className="theme-section">
        <h4 className="form-section-title">Banner Image</h4>
        <p className="helper-text">
          Upload a banner image for your website header. Recommended size: 1200x400 pixels.
        </p>

        <div className="image-upload-section">
          <div className="image-preview banner">
            {bannerUrl ? (
              <img src={bannerUrl} alt="Banner" />
            ) : (
              <span style={{ color: '#999' }}>No banner</span>
            )}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files[0], 'banner')}
              style={{ display: 'none' }}
              disabled={isUploading}
            />
            <FormButton type="button" disabled={isUploading} onClick={() => bannerInputRef.current?.click()}>
              <UploadIcon style={{ marginRight: 5, fontSize: 18 }} />
              {isUploading ? 'Uploading...' : 'Upload Banner'}
            </FormButton>
            {bannerUrl && (
              <IconButton onClick={() => handleRemoveImage('banner')} size="small">
                <DeleteIcon />
              </IconButton>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ marginTop: 30 }}>
        <FormButton onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Theme Settings'}
        </FormButton>
      </div>
    </div>
  );
};

export default ThemeEditor;
